"use client";

import { useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";

const IMAGE_BASE = "/image/new-version/casting/High-Pressure-Die-Casting/Materials-Finish";

const ALLOYS = [
	{
		keyPrefix: "materialsFinishAlloy1",
		image: `${IMAGE_BASE}/Materials/die-casting-zinc-alloy.png`,
		gradeCount: 4,
	},
	{
		keyPrefix: "materialsFinishAlloy2",
		image: `${IMAGE_BASE}/Materials/die-casting-aluminum-alloy.png`,
		gradeCount: 5,
	},
	{
		keyPrefix: "materialsFinishAlloy3",
		image: `${IMAGE_BASE}/Materials/die-casting-copper-alloy.png`,
		gradeCount: 3,
	},
	{
		keyPrefix: "materialsFinishAlloy4",
		image: `${IMAGE_BASE}/Materials/high-end-aluminum-alloy.png`,
		gradeCount: 4,
	},
] as const;

const FINISHES = [
	{
		keyPrefix: "materialsFinishFinish1",
		image: `${IMAGE_BASE}/Surface Finish/as-cast.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish2",
		image: `${IMAGE_BASE}/Surface Finish/anodizing.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish3",
		image: `${IMAGE_BASE}/Surface Finish/hard-anodizing.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish4",
		image: `${IMAGE_BASE}/Surface Finish/chromate-passivation.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish5",
		image: `${IMAGE_BASE}/Surface Finish/painting.webp`,
	},
	{
		keyPrefix: "materialsFinishFinish6",
		image: `${IMAGE_BASE}/Surface Finish/sand-blasting.webp`,
	},
] as const;

export default function MaterialsAndFinishes() {
	const t = useTranslations("new-high-pressure-casting");
	const [activeTab, setActiveTab] = useState<"materials" | "finishes">("materials");
	const [activeAlloy, setActiveAlloy] = useState(0);

	return (
		<section className="bg-[#f6f6f6] py-8 sm:py-10 md:py-12 lg:py-16">
			<div className="container">
				<h2 className="mb-3 text-center text-2xl font-bold leading-tight text-black sm:mb-4 sm:text-[30px] md:text-4xl">
					{t("materialsFinishTitle")}
				</h2>

				<div className="mb-3 flex justify-center gap-3 sm:mb-4">
					<button
						type="button"
						onClick={() => setActiveTab("materials")}
						className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors sm:px-6 sm:text-base ${
							activeTab === "materials"
								? "border-themePrimary400 bg-themePrimary400 text-white"
								: "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
						}`}
					>
						{t("materialsFinishTabMaterials")}
					</button>
					<button
						type="button"
						onClick={() => setActiveTab("finishes")}
						className={`rounded-full border px-5 py-2 text-sm font-semibold transition-colors sm:px-6 sm:text-base ${
							activeTab === "finishes"
								? "border-themePrimary400 bg-themePrimary400 text-white"
								: "border-gray-300 bg-white text-gray-700 hover:border-gray-400"
						}`}
					>
						{t("materialsFinishTabFinishes")}
					</button>
				</div>

				<p className="mx-auto mb-6 max-w-3xl text-center text-sm leading-relaxed text-black sm:mb-8 md:mb-10 md:text-base lg:mb-12">
					{activeTab === "materials" ? t("materialsFinishMaterialsDesc") : t("materialsFinishFinishesDesc")}
				</p>

				{activeTab === "materials" ? (
					<MaterialsPanel t={t} activeAlloy={activeAlloy} setActiveAlloy={setActiveAlloy} />
				) : (
					<FinishesPanel t={t} />
				)}
			</div>
		</section>
	);
}

function MaterialsPanel({
	t,
	activeAlloy,
	setActiveAlloy,
}: {
	t: ReturnType<typeof useTranslations<"new-high-pressure-casting">>;
	activeAlloy: number;
	setActiveAlloy: (i: number) => void;
}) {
	const alloy = ALLOYS[activeAlloy]!;

	return (
		<div>
			<h3 className="mb-4 text-lg font-bold text-black sm:mb-6 sm:text-xl md:text-2xl">
				{t("materialsFinishAlloysTitle")}
			</h3>
			<div className="mb-10 border-t-2 border-themePrimary400" />

			<div className="grid grid-cols-1 lg:grid-cols-[280px_1fr_1fr] lg:gap-x-6">
				{/* Left sidebar */}
				<div className="flex flex-row gap-0 overflow-x-auto border-b border-gray-200 lg:flex-col lg:overflow-x-visible lg:border-b-0">
					{ALLOYS.map((a, i) => (
						<button
							key={a.keyPrefix}
							type="button"
							onClick={() => setActiveAlloy(i)}
							className={`whitespace-nowrap px-4 py-3 text-left text-sm font-medium transition-colors sm:py-4 sm:text-base ${
								activeAlloy === i
									? "bg-themePrimary400 font-bold text-white"
									: "bg-[#f6f6f6] text-gray-700 hover:bg-gray-50"
							}`}
						>
							{t(`${a.keyPrefix}Name` as any)}
						</button>
					))}
				</div>

				{/* Center image */}
				<div className="flex items-center justify-center bg-[#bfbebf]">
					<div className="relative aspect-[4/2.5] w-full max-w-md">
						<Image
							src={alloy.image}
							alt={t(`${alloy.keyPrefix}ImageAlt` as any)}
							fill
							className="object-cover"
							sizes="(max-width: 1024px) 100vw, 33vw"
						/>
					</div>
				</div>

				{/* Right description */}
				<div className="flex flex-col justify-center p-6 sm:p-8 md:p-10">
					<h4 className="mb-3 text-lg font-bold text-black sm:mb-4 sm:text-xl md:text-2xl">
						{t(`${alloy.keyPrefix}Name` as any)}
					</h4>
					<p className="mb-4 text-sm leading-relaxed text-black sm:mb-6 md:text-base">
						{t(`${alloy.keyPrefix}Desc` as any)}
					</p>
					<ul className="space-y-1.5">
						{Array.from({ length: alloy.gradeCount }, (_, gi) => (
							<li key={gi} className="flex items-center gap-2 text-sm text-gray-800 md:text-base">
								<span className="inline-block size-1.5 shrink-0 rounded-full bg-gray-800" />
								{t(`${alloy.keyPrefix}Grade${gi + 1}` as any)}
							</li>
						))}
					</ul>
				</div>
			</div>
		</div>
	);
}

function FinishesPanel({ t }: { t: ReturnType<typeof useTranslations<"new-high-pressure-casting">> }) {
	return (
		<div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6">
			{FINISHES.map((card) => (
				<article
					key={card.keyPrefix}
					className="flex flex-col overflow-hidden rounded-md border border-[#eeeeee] bg-[#eeeeee]"
				>
					<div className="relative aspect-[4/2.3] w-full bg-white">
						<Image
							src={card.image}
							alt={t(`${card.keyPrefix}ImageAlt` as any)}
							fill
							className="object-contain p-4"
							sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
						/>
					</div>
					<div className="flex flex-1 flex-col p-5 sm:p-6">
						<h3 className="mb-2 text-lg font-bold text-black sm:mb-3 sm:text-xl md:text-2xl">
							{t(`${card.keyPrefix}Title` as any)}
						</h3>
						<p className="text-sm leading-relaxed text-black md:text-base">
							{t(`${card.keyPrefix}Desc` as any)}
						</p>
					</div>
				</article>
			))}
		</div>
	);
}
